import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { saveTopicSuggestion } from '../../lib/accountNotes';
import { fetchAllJourneyPaths } from '../../lib/contentClient';
import { featuredPath, learnChips, newReleasePaths, searchPaths, topicTextOk, type ExplorePath } from '../../lib/exploreModel';
import { lessonProgressService } from '../../lib/lessonProgressService';
import { NEED_CHOICES } from '../../lib/onboardingModel';
import { loadRecord } from '../../lib/onboardingStore';

export default function ExplorePage() {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [topic, setTopic] = useState('');
  const [note, setNote] = useState('');
  const pathsQuery = useQuery({ queryKey: ['journey-paths'], queryFn: () => fetchAllJourneyPaths() });
  const progressQuery = useQuery({
    queryKey: ['lesson-progress', user?.id || 'device'],
    queryFn: () => lessonProgressService.getCompletedLessons(),
  });
  const completed = useMemo(
    () => [...(progressQuery.data || lessonProgressService.getCompletedLessonsFromLocal())],
    [progressQuery.data],
  );
  const paths: ExplorePath[] = (pathsQuery.data || []).map((path) => ({
    slug: path.slug,
    title: path.primaryLanguage === 'en' ? path.titleEn || path.title : path.title,
    launchReady: path.launchReady === true,
    premium: Boolean(path.isPremium),
    lessonIds: (path.modules || []).flatMap((module) => (module.lessons || []).map((lesson) => lesson._id)),
  }));
  const results = searchPaths(paths, query);
  const chips = learnChips(paths, NEED_CHOICES.map((choice) => ({ slug: choice.slug, label: choice.en })));
  const releases = newReleasePaths(paths, completed);
  const featured = featuredPath(paths, loadRecord()?.journeySlug ?? null);

  const sendTopic = async () => {
    const result = await saveTopicSuggestion({ userId: user?.id ?? null, topic });
    if (result === 'invalid') {
      setNote('Use 2 to 200 characters.');
      return;
    }
    setNote(result === 'saved' ? 'Saved.' : 'Saved on this device.');
    setTopic('');
  };

  return (
    <div className="font-myanmar mx-auto w-full max-w-lg min-w-0 overflow-x-hidden px-4 py-6" data-testid="explore-page">
      <h1 className="text-2xl font-semibold text-gray-900">Explore</h1>
      <p className="lesson-subtext text-sm text-gray-600">ရှာဖွေရန်</p>
      <label className="mt-4 block text-sm font-semibold" htmlFor="explore-search">
        Search
        <input
          id="explore-search"
          className="mt-2 block min-h-[48px] w-full rounded-2xl border border-gray-300 bg-white px-3"
          data-testid="explore-search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      {query.trim() ? (
        <ul className="mt-3 space-y-2">
          {results.length === 0 ? <li className="text-sm text-gray-600">No matching path.</li> : null}
          {results.map((path) => (
            <li key={path.slug}>
              <Link to={`/courses/${path.slug}`} className="block break-words rounded-2xl bg-white px-4 py-3" data-testid="explore-result">
                {path.title}
                {path.launchReady ? '' : ' · Not open yet'}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}

      <section className="mt-6">
        <h2 className="text-lg font-semibold">I want to learn about</h2>
        <div className="mt-2 flex flex-wrap gap-2">
          {chips.map((chip) => (
            <Link
              key={chip.slug}
              to={`/courses/${chip.slug}`}
              className="min-h-[44px] rounded-full bg-white px-4 py-2 text-sm font-semibold"
              data-testid="explore-chip"
              data-slug={chip.slug}
              data-open={chip.open ? '1' : '0'}
            >
              {chip.label}
              {chip.open ? '' : ' · Not open yet'}
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-6" data-testid="explore-new">
        <h2 className="text-lg font-semibold">New releases</h2>
        <p className="text-sm text-gray-600">Open paths you have not finished.</p>
        {pathsQuery.isLoading ? <p className="mt-2 text-sm">Loading…</p> : null}
        {!pathsQuery.isLoading && releases.length === 0 ? (
          <p className="mt-2 text-sm">No new paths. You have started the open ones.</p>
        ) : null}
        <ul className="mt-2 space-y-2">
          {releases.map((path) => (
            <li key={path.slug}>
              <Link to={`/courses/${path.slug}`} className="block break-words rounded-2xl bg-white px-4 py-3" data-testid="explore-release">
                {path.title}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-6" data-testid="explore-featured">
        <h2 className="text-lg font-semibold">Featured</h2>
        {featured.path ? (
          <Link to={`/courses/${featured.path.slug}`} className="mt-2 block break-words rounded-2xl bg-white px-4 py-3">
            <span className="font-semibold">{featured.path.title}</span>
            <span className="mt-1 block text-sm text-gray-600">{featured.reason === 'yours' ? 'Your path' : 'A free path that is open.'}</span>
          </Link>
        ) : (
          <p className="mt-2 text-sm text-gray-600">No open path to feature.</p>
        )}
      </section>

      <section className="mt-6 rounded-2xl bg-white px-4 py-4" data-testid="explore-suggest">
        <h2 className="text-lg font-semibold">Suggest a topic</h2>
        <textarea
          className="mt-2 min-h-[96px] w-full rounded-2xl border border-gray-300 px-3 py-2 text-sm"
          data-testid="explore-suggest-text"
          value={topic}
          maxLength={200}
          onChange={(event) => setTopic(event.target.value)}
        />
        <button
          type="button"
          className="mt-2 min-h-[48px] w-full rounded-2xl bg-teal-700 text-sm font-semibold text-white disabled:opacity-50"
          data-testid="explore-suggest-send"
          disabled={!topicTextOk(topic)}
          onClick={() => void sendTopic()}
        >
          Send
        </button>
        {note ? <p className="mt-2 text-sm" data-testid="explore-suggest-note">{note}</p> : null}
      </section>
    </div>
  );
}
